import React, {useState, useEffect} from 'react';
import {
    BrowserRouter as Router,
    Switch,
    Route
} from "react-router-dom";
import {BASE_URL} from './env.json';
import ErrorPage from './pages/errorPage';
import axios from 'axios';
import * as Types from './styles/types';
import Colors from './styles/colors';
import {makeStyles} from '@material-ui/styles';

//v2
import Intro from "./pages/Intro";
import WaitScreen from "./components/WaitScreen";
import Home from "./pages/Home";

//sass
import "./styles/sass/ascend.css";
import TasksList from "./pages/TasksList";
import TasksView from "./pages/TasksView";

//Define our types for the context:
export type ContextProps = {
    screenContent: (Types.Content) | any
    setActiveWeekTaskList: (Types.WeekTask) | any
    activeWeekTaskList: (Types.WeekTask) | any
    setActiveTask: (Types.Task) | any
    activeTask: (Types.Task) | any
    lessonList: (Types.LessonPlanLesson)[] | null,
    lessonPlan: Types.LessonPlan,
    userStatus: Types.UserStatus,
    questionResponse: (Types.QuestionResponsesEntity)[] | null,
    relapses: (Types.RelapseReportEntity)[] | null,
    checkIns: (Types.WeeklyCheckInsEntity)[] | null,
    isLoading: boolean,
    currentLesson: number,
    waitPeriodEnd: string | Date,
    deviceId: string,
    checkRefs: boolean,
    recoveryId: string,
    isAdmin: boolean,
    hasError: { state: boolean, value: string },
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
    setDeviceId: React.Dispatch<React.SetStateAction<string>>,
    setCheckRefs: React.Dispatch<React.SetStateAction<boolean>>,
    setRecoveryId: React.Dispatch<React.SetStateAction<string>>,
    setHasErrors: React.Dispatch<React.SetStateAction<{
        state: boolean;
        value: string;
    }>>,
    setWaitPeriodEnd: React.Dispatch<React.SetStateAction<string | Date>>
    appContent: (string | any)[],
    currentWeekNo: number,
    currentTaskNo: number,
    currentStepNo: number,
    currentLessonNo: number,
    setCurrentWeekNo: number | any,
    setCurrentTaskNo: number | any,
    setCurrentStepNo: number | any,
    setCurrentLessonNo: number | any,
    quizResponse: (string | any)[],
    setQuizResponse: string | any,
    surveyResult: string,
    setSurveyResult: string | any,
    isCompleted: boolean,
    setIsCompleted: boolean | any,
    userId: string,
    setUserId: string | any,
};

export const AppContext = React.createContext<Partial<ContextProps>>({});

const RecoveryApp = () => {
    const regex = /D-(.*)/i;
    const [deviceId, setDeviceId] = useState<string>(window.location.pathname.split('/').filter(item => regex.test(item))[0]);
    const userRegex = /RU-(.*)/i;
    const [recoveryId, setRecoveryId] = useState<string>(window.location.pathname.split('/').filter(item => userRegex.test(item))[0]);

    //init global states.
    const [userStatus, setUserStatus] = useState<Types.UserStatus | any>({});
    const [questionResponse, setQuestionResponse] = useState<(Types.QuestionResponsesEntity)[] | null>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [relapses, setRelapses] = useState<(Types.RelapseReportEntity)[] | null>([]);
    const [checkIns, setCheckIns] = useState<(Types.WeeklyCheckInsEntity)[] | null>([]);
    const [waitPeriodEnd, setWaitPeriodEnd] = useState<string | Date>('');
    const [checkRefs, setCheckRefs] = useState<boolean>(true);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [hasError, setHasError] = useState({state: false, value: ''});

    //v2
    const [currentTask, setCurrentTask] = useState<string>('');
    const [currentWeek, setCurrentWeek] = useState<number>(0);
    const [screenContent, setScreenContent] = useState<(Types.Content) | any>({});
    const [activeWeekTaskList, setActiveWeekTaskList] = useState<(Types.WeekTask) | any>({});
    const [activeTask, setActiveTask] = useState<(Types.Task) | any>({});

    const [currentWeekNo, setCurrentWeekNo] = useState<number>(0);
    const [currentTaskNo, setCurrentTaskNo] = useState<number>(0);
    const [currentStepNo, setCurrentStepNo] = useState<number>(0);
    const [currentLessonNo, setCurrentLessonNo] = useState<number>(0);
    const [appContent, setAppContent] = useState<string | any>({});
    const [quizResponse, setQuizResponse] = useState<string | any>({});
    const [surveyResult, setSurveyResult] = useState<string>('');
    const [isCompleted, setIsCompleted] = useState<boolean>(false);
    const [userId, setUserId] = useState<string>('');

    //Create Global "Store"
    const store = {
        screenContent: screenContent,
        userStatus: userStatus,
        questionResponse: questionResponse,
        isLoading: isLoading,
        setIsLoading: setIsLoading,
        relapses: relapses,
        checkIns: checkIns,
        waitPeriodEnd: waitPeriodEnd,
        deviceId: deviceId,
        setDeviceId: setDeviceId,
        checkRefs: checkRefs,
        recoveryId: recoveryId,
        setRecoveryId: setRecoveryId,
        isAdmin: isAdmin,
        hasError: hasError,
        setHasError: setHasError,
        currentTask: currentTask,
        currentWeek: currentWeek,
        activeWeekTaskList: activeWeekTaskList,
        setActiveWeekTaskList: setActiveWeekTaskList,
        activeTask: activeTask,
        setActiveTask: setActiveTask,
        currentWeekNo: currentWeekNo,
        setCurrentWeekNo: setCurrentWeekNo,
        currentTaskNo: currentTaskNo,
        setCurrentTaskNo: setCurrentTaskNo,
        currentLessonNo: currentLessonNo,
        setCurrentLessonNo: setCurrentLessonNo,
        appContent: appContent,
        setAppContent:setAppContent,
        currentStepNo:currentStepNo,
        setCurrentStepNo:setCurrentStepNo,
        quizResponse: quizResponse,
        setQuizResponse: setQuizResponse,
        surveyResult: surveyResult,
        setSurveyResult: setSurveyResult,
        isCompleted: isCompleted,
        setIsCompleted: setIsCompleted,
        userId: userId,
        setUserId: setUserId,
    };

    // set axios defaults
    //set csrf token defaults
    axios.defaults.xsrfHeaderName = "X-CSRFTOKEN"
    axios.defaults.xsrfCookieName = "csrftoken"
    axios.defaults.withCredentials = true

    // eslint-disable-next-line react-hooks/exhaustive-deps
    async function get_data(_callback: { (): void; (): void; }) {
        // this is just to get the csrftoken
        axios.get(BASE_URL + `recovery2/recovery2-user/${recoveryId}/`)
            .then((response) => {
                axios.defaults.headers = {'X-CSRFTOKEN': response.headers['x-csrftoken']};
            });

        //URLS
        const userEndpoint = BASE_URL + `recovery2/recovery2-user/${recoveryId}/`;
        //TODO: IF userObject fails, show some sort of message.
        const userObject = await axios.get(userEndpoint)
            .then((response) => {
                axios.defaults.headers = {'X-CSRFTOKEN': response.headers['x-csrftoken']};
                return response.data
            })
            .catch((err) => {
                console.log(err);
                setHasError({
                    state: true,
                    value: "The app could not validate who you are. Please contact support if this continues to happen.",
                });
            });


        let appContentEndpoint = 'recovery2/tasks/';
        const appContentObj = await axios.get(BASE_URL + appContentEndpoint)
            .then((response) => {
                return response.data
            })
            .catch((err) => {
                console.log(err);
                setHasError({
                    state: true,
                    value: "Could not retrieve Week Content. Please contact support if this continues to happen",
                });
            });

        if(appContentObj !== undefined) {
            console.log("APP CONTENT")
            setAppContent(appContentObj)
            console.log(appContentObj)
        }

        console.log(userObject);
        const stepsEndpoint = `recovery2/steps/?task_id=${userObject.current_task.id}`;
        const stepsObj = await axios.get(BASE_URL + stepsEndpoint)
            .then((response) => {
                return response.data
            })
            .catch((err) => {
                console.log(err);
                setHasError({
                    state: true,
                    value: "Could not retrieve App Content. Please contact support if this continues to happen",
                });
            });

        //update the state
        if (userObject !== undefined) {
            setUserStatus(userObject);
            setCheckIns((userObject.weekly_check_ins) ? userObject.weekly_check_ins : []);
            setQuestionResponse(userObject.question_responses);
            setRelapses((userObject.relapse_report) ? userObject.relapse_report : []);
            setWaitPeriodEnd(userObject.wait_period_end);
            setIsAdmin(userObject.user.person.is_admin);

            //v2
            setCurrentTask(userObject.current_task);
            setCurrentWeek(userObject.current_task.week.id)
            setScreenContent({
                week: userObject.current_task.week,
                task: userObject.current_task.task,
                steps: stepsObj,
            });

            setCurrentWeekNo(userObject.current_task.week.ordering_number)
            setCurrentTaskNo(userObject.current_task.ordering_number)
            setIsCompleted(userObject.program_complete)
            setUserId(userObject.user.id)
        }

        //callback to change states for loading and refs
        _callback();
    }

    //Create our effect which replaces componentDidMount and componentDidUnmount
    useEffect(() => {
        if (isLoading) {
            console.log('get_data running')
            get_data(() => {
                setIsLoading(false);
                setCheckRefs(true);
            });
        }

        return () => {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    const styles = style()

    return (
        <AppContext.Provider value={store}>
            <Router>
                <div className={styles.fullHeightHidden}>
                    {(isLoading) ? (
                       <WaitScreen/>
                    ) : (
                        <Switch>
                            <Route path='/home/:device_id/:recovery_id' component={Home}/>
                            <Route path='/task-list/:device_id/:recovery_id' component={TasksList}/>
                            <Route path='/task-view/:device_id/:recovery_id' component={TasksView}/>
                            <Route path='/intro/:device_id/:recovery_id' component={Intro}/>
                            <Route path='/wait/:device_id/:recovery_id' component={WaitScreen}/>
                            <Route component={ErrorPage}/>
                        </Switch>
                    )
                    }
                </div>
            </Router>
        </AppContext.Provider>
    );
};

export default RecoveryApp;

const style = makeStyles({
    fullHeightHidden: {
        backgroundColor: Colors.white,
        height: '100%',
        overflow: 'hidden',
    },
    progressIndicator: {
        padding: 20,
        height: 500,
        overFlow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    }
});
